import { useSidebarStore } from "@/atoms/playground";
import { ExternalLink, MessagesSquare, PanelsTopLeft, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SheetTrigger } from "@/components/ui/sheet";
import { usePlaygroundPresentation } from "./playground-presentation";
import { useThemeMode } from "@/contexts/ThemeContext";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";
import { CONFIDENTIAL_ROUTE_ID } from "@/atoms/confidential-session";
import { usePlaygroundRouting } from "../routing/playground-routing";
import { useHistorySidebarAccessibility } from "./history-sidebar-accessibility";

export const PlaygroundNavbar = ({ title, id, actions }: { title: string; id: string; actions?: React.ReactNode }) => {
  const { t } = useTranslation();
  const sidebarState = useSidebarStore();
  const { modern, mobile, mobileHistoryOpen, settingsOpen, settingsId, openSettings, closeSettings } = usePlaygroundPresentation();
  const accessibility = useHistorySidebarAccessibility();
  const { buildStandaloneDestination, routeNamespace } = usePlaygroundRouting();
  const storedTheme = useStoredTheme();
  const { mode } = useThemeMode();
  const buttonStyle = {
    "--bg": storedTheme.button.primary.backgroundColor[mode],
    "--hover-bg": storedTheme.button.primary.hoverBackgroundColor[mode],
  } as React.CSSProperties;

  return (
    <div
      className={modern ? "relative z-10 flex min-h-[60px] w-full shrink-0 items-center justify-between gap-2 bg-background px-4 py-2" : "absolute top-0 start-0 z-10 flex w-full items-center justify-between p-[10px] pb-5 min-[600px]:p-4 min-[600px]:pb-6"}
      style={modern ? undefined : { background: "linear-gradient(180deg, var(--card-background) 40%, transparent 100%)" }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden me-4">
        {!modern && !sidebarState.isOpen && (
          <button ref={accessibility?.opener} type="button" aria-label={t("playground.openSidebar")} onClick={sidebarState.toggle}
            aria-expanded={sidebarState.isOpen} aria-controls={accessibility?.id}
            className="flex size-[34px] items-center justify-center rounded-full bg-[var(--bg)] transition-colors hover:bg-[var(--hover-bg)] focus-visible:outline-2 focus-visible:outline-ring" style={buttonStyle}>
            <PanelsTopLeft size={18} />
          </button>
        )}
        {routeNamespace !== "playground" && (
          <h2 className="truncate text-sm font-medium leading-[1.167] tracking-[-0.01562em] min-[600px]:text-base min-[600px]:leading-[1.167]" style={{ color: storedTheme.header.textColor[mode] }}>{title}</h2>
        )}
      </div>
      <div className={modern ? "flex shrink-0 items-center gap-2" : "flex shrink-0 items-center gap-3"}>
        {actions}
        {modern && (mobile ? <SheetTrigger asChild>
          <Button ref={accessibility?.opener} type="button" variant="ghost" size="icon" className="motion-reduce:transition-none"
            aria-label={t(mobileHistoryOpen ? "playground.closeSidebar" : "playground.openSidebar")}
            aria-expanded={mobileHistoryOpen} aria-controls={accessibility?.id}>
            <MessagesSquare aria-hidden="true" className="size-5" />
          </Button>
        </SheetTrigger> : <Button ref={accessibility?.opener} type="button" variant="ghost" size="icon" className="motion-reduce:transition-none"
          aria-label={t(sidebarState.isOpen && !settingsOpen ? "playground.closeSidebar" : "playground.openSidebar")}
          aria-expanded={sidebarState.isOpen && !settingsOpen} aria-controls={accessibility?.id} onClick={() => {
            if (settingsOpen) { closeSettings(); if (!sidebarState.isOpen) sidebarState.toggle(); }
            else sidebarState.toggle();
          }}>
          <MessagesSquare aria-hidden="true" className="size-5" />
        </Button>)}
        {modern && !mobile && <Button type="button" variant="ghost" size="icon"
          aria-label={t("playground.runSettings")} aria-expanded={settingsOpen} aria-controls={settingsId}
          className="motion-reduce:transition-none" onClick={event => {
            if (settingsOpen) closeSettings(); else openSettings(event.currentTarget);
          }}>
          <SlidersHorizontal aria-hidden="true" className="size-4" />
        </Button>}
        {routeNamespace === "playground" && (modern ? (
          <Button asChild variant="ghost" size="icon" className="rounded-lg motion-reduce:transition-none">
            <a href={buildStandaloneDestination(id)} target="_blank" rel="noopener noreferrer"
              data-confidential-leave-guard={id === CONFIDENTIAL_ROUTE_ID ? "true" : undefined}
              aria-label={t("playground.openInNewTab")}>
              <ExternalLink aria-hidden="true" className="size-[16.25px]" />
            </a>
          </Button>
        ) : (
          <a href={buildStandaloneDestination(id)} target="_blank" rel="noopener noreferrer"
            data-confidential-leave-guard={id === CONFIDENTIAL_ROUTE_ID ? "true" : undefined}
            aria-label={t("playground.openInNewTab")} className="rounded-full focus-visible:outline-2 focus-visible:outline-ring">
            <div className="flex size-[34px] items-center justify-center rounded-full bg-[var(--bg)] p-1 hover:bg-[var(--hover-bg)]" style={buttonStyle}>
              <ExternalLink color={storedTheme.button.primary.textColor[mode]} />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};
